import React, { useEffect, useState } from "react";
import { ReactComponent as PlusIcon } from "static/icons/plus-circle.svg";
import { ReactComponent as MinusIcon } from "static/icons/minus-circle.svg";
import Button from "components/Buttons/Button";
import Collapse from "components/Collapse";
import useSelector from "hooks/useSelector";
import MarkdownContent from "components/Markdown";
import styles from "./Todo.module.scss";
import useBreakPoint from "hooks/useBreakPoint";
import { Breakpoint } from "../../types/breakpoint";
import classNames from "classnames";
import { useIntl } from "react-intl";
import MobileModal from "components/Modals/MobileModal";

const TodoItem = ({
    title,
    description,
    buttonText = "approve",
    buttonTarget,
    expandItems,
    idExpand,
    idButton,
}: {
    title: string;
    description: string;
    buttonText: string;
    buttonTarget: string;
    expandItems: boolean;
    idExpand?: string;
    idButton?: string;
}) => {
    const isMobile = useBreakPoint(Breakpoint.mobile);
    const [open, setOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const intl = useIntl();
    useEffect(() => {
        setOpen(expandItems);
    }, [expandItems]);
    function onToggle() {
        setOpen(!open);
    }
    function onModalClick() {
        setModalOpen(true);
    }

    return (
        <li
            className={classNames(styles["item"], {
                [styles["item--mobile"]]: isMobile,
            })}
        >
            <div
                className={classNames(styles["item__header"], {
                    [styles["item--header__mobile"]]: isMobile,
                })}
            >
                <button onClick={onToggle} className={styles["item__header--primary"]}>
                    <p className={styles["item__title"]}>
                        {open ? (
                            <MinusIcon className={styles["item__icon"]} />
                        ) : (
                            <PlusIcon id={idExpand} className={styles["item__icon"]} />
                        )}{" "}
                        {title}
                    </p>
                </button>
                <Button
                    size="small"
                    variant="secondary"
                    className={styles["button--desktop"]}
                    to={buttonTarget}
                    text={buttonText}
                    id={idButton}
                />
            </div>

            <div className={styles["item--mobile__header"]}>
                <p className={styles["item--mobile__title"]}>{title}</p>
                <button onClick={onModalClick} className={styles["item--mobile__button"]}>
                    {intl.formatMessage({ id: "home.todo.item.read_more" })}
                </button>
                {modalOpen && (
                    <MobileModal
                        title={title}
                        description={description}
                        buttonText={buttonText}
                        buttonTarget={buttonTarget}
                        onClose={() => setModalOpen(false)}
                    />
                )}
            </div>

            <Collapse.CollapseItem open={open}>
                <MarkdownContent content={description} className={styles["item__description"]} />
            </Collapse.CollapseItem>
        </li>
    );
};

const Todo = () => {
    const intl = useIntl();
    const recommends = useSelector((state) => state.recommends.data);
    const [expandItems, setExpandItems] = useState<boolean>(false);
    if (!recommends) {
        return null;
    }
    const onExpandItems = () => {
        setExpandItems(!expandItems);
    };

    return (
        <div className={styles["todo"]}>
            <div className={styles["todo__header"]}>
                {intl.formatMessage({
                    id: "hr_recommends.title",
                })}
                <Button
                    size="small"
                    className={styles["todo__btn"]}
                    text={expandItems ? "Collapse All" : "Expand All"}
                    onClick={onExpandItems}
                />
            </div>
            <ul className={styles["todo__list"]}>
                {recommends.recommend.map((todo, index) => {
                    const id: number = index + 1;
                    return (
                        <TodoItem
                            key={todo?.id}
                            idExpand={"MyHR-Recommends-expand-" + id}
                            idButton={"MyHR-Recommends-button-" + id}
                            title={todo?.displayName || ""}
                            description={todo?.description || ""}
                            buttonText={todo?.actionText || ""}
                            buttonTarget={todo?.actionTarget || ""}
                            expandItems={expandItems}
                        />
                    );
                })}
            </ul>
        </div>
    );
};

export default Todo;
